import { PageEvent } from '@angular/material/paginator';
import { Params } from '@angular/router';
import { ProductPricing } from '@galaxy/billing';
import { AppPrice, AppPrices } from '@galaxy/marketplace-apps';
import { EditionIdentifier } from '@galaxy/marketplace-apps/v1';
import {
  AppAndAddonActivationStatus,
  ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface,
  PendingActivationStatus,
} from '@vendasta/accounts/legacy';
import { LineItem, Package } from '@vendasta/marketplace-packages';
import { ResellerItem } from '@vendasta/store';
import { Pricing } from '../../common/table-pricing/interface';
import { convertPricingToTablePricing } from '../../common/table-pricing/utils';
import { Activation } from '../../core/account.service';
import { PurchaseCost } from '../../core/billing/purchase-cost';
import { getResellerItemUniqueId, resellerItemUsesOrderForm } from '../../core/reseller/utils';
import { DynamicOrderFormAppIds } from '../../marketplace-app/global-brands-constants';
import { ItemIdAndQuantity, UniqueIdToItemIdAndQuantityMap } from '../order-form-page/interface';
import {
  ActivatableItem,
  ActivatableItemCount,
  ActivatablePackage,
  ActivationStatus,
  Edition,
  EditionData,
  isValidEditionId,
  OrderFormType,
  ProductBoxItems,
  ProductEditionMap,
  QuantityEditionIdPair,
  SelectedItem,
  SelectedItemQueryParams,
  SelectedItems,
  SortDirection,
} from './interface';
import { PendingActivationDisplay } from './pending-activations.service';

export function convertResellerItemsToFlatActivatableItems(
  resellerItems: ResellerItem[],
  pricingMap: Map<string, ProductPricing>,
  country: string,
  partnerFeatures: string[],
  activeStates: ActivationStatusesInterface[],
  editionsMap: ProductEditionMap,
  pendingActivations: PendingActivationDisplay[],
  appPriceMap: Map<string, AppPrices>,
  partnerCurrency: string,
): ActivatableItem[] {
  const resellerItemsWithPricing = pairResellerItemsWithPricing(resellerItems, pricingMap, appPriceMap);
  const resellerItemsWithPricingAndStatus = pairResellerPricingWithActiveStatuses(
    resellerItemsWithPricing,
    activeStates,
    pendingActivations,
  );
  return (resellerItemsWithPricingAndStatus || []).map((ri) => {
    const idToCheck = ri.resellerItem.addonId ? ri.resellerItem.addonId : ri.resellerItem.appId;
    let editions = null;
    if (editionsMap) {
      editions = editionsMap.get(idToCheck) || null;
    }
    return convertResellerItemToActivatableItem(
      ri.resellerItem,
      ri.pricing,
      ri.pricingPurchaseCost,
      country,
      partnerFeatures,
      ri.activeStatuses,
      editions,
      ri.appPrice,
      partnerCurrency,
    );
  });
}

export function nestActivatableItems(activatableItems: ActivatableItem[]): ActivatableItem[] {
  if (!activatableItems || activatableItems.length === 0) {
    return [];
  }
  const parentApps = getAppsWithoutParents(activatableItems);
  const childMap = buildChildMap(activatableItems);
  const nestedItems = setChildAppsOnClonedParentApps(parentApps, childMap);
  return applyRestrictedReasonToChildren(nestedItems);
}

export function convertResellerItemToActivatableItem(
  resellerItem: ResellerItem,
  pricing: ProductPricing,
  pricingPurchaseCost: PurchaseCost,
  country: string,
  partnerFeatures: string[],
  activeState: ActivationStatusesInterface[],
  editions: Edition[],
  appPrice: AppPrice,
  partnerCurrency: string,
): ActivatableItem {
  const valueTotalCost = pricingPurchaseCost && pricingPurchaseCost ? pricingPurchaseCost.totalCost : 0;

  let convertedPricing = convertPricingToTablePricing(pricing, valueTotalCost);
  // if billing doesn't have a pricing model for the product, fall back to the products pricing
  if (!convertedPricing) {
    convertedPricing = {
      currency: resellerItem.currency,
      frequency: resellerItem.billingFrequency,
      prices: [{ price: resellerItem.price }],
      purchaseCostTotal: valueTotalCost,
    } as Pricing;
  }
  const withinCountryRestrictions = isCountryAllowedByResellerItemCountry(resellerItem, country);
  const partnerFeaturesValid = partnerHasRequiredFeatures(resellerItem, partnerFeatures);
  let restrictedReason: string;
  if (!withinCountryRestrictions) {
    restrictedReason = `Unavailable in ${country}`;
  } else if (!partnerFeaturesValid) {
    restrictedReason = 'Subscription upgrade required';
  }

  // For now, don't support AND and OR logic, just support a single set of required app IDs.
  // Also, edition IDs need to be mapped because pro editions are often blank, which are returned as undefined.
  const appRules = resellerItem.restrictions?.appRequirement?.rules ?? [];
  const requiredAppIds = appRules[0]?.appIds ?? [];
  const requiredEditions = (appRules[0]?.requiredEditions ?? []).map((rei) => ({
    appId: rei.appId,
    editionId: rei.editionId ?? '',
  }));
  const parentAppId = resellerItem.addonId ? resellerItem.appId : '';
  return {
    appId: getResellerItemUniqueId(resellerItem),
    requiredAppIds: requiredAppIds,
    requiredEditions: requiredEditions,
    parentAppId: parentAppId,
    // editions not handled here as we don't know WHICH edition the item is at this point
    billingId: resellerItem.billingId,
    name: resellerItem.name,
    icon: resellerItem.icon,
    allowMultipleActivations: resellerItem.allowMultipleActivations,
    pricing: convertedPricing,
    activeState: activeState,
    usesOrderForm: resellerItemUsesOrderForm(resellerItem),
    restrictedReason: restrictedReason,
    editionData: setUpEditionsData(editions),
    usesCustomPricing: resellerItem.usesCustomPricing,
    appPrice: appPrice,
    partnerCurrency: partnerCurrency,
  } as ActivatableItem;
}

function setUpEditionsData(editions: Edition[]): EditionData {
  if (!(editions && editions.length > 0)) {
    return {
      usesEditions: false,
    } as EditionData;
  }

  return {
    usesEditions: true,
    editions: editions,
  } as EditionData;
}

function getEditionBillingId(appId: string, editionId: string, editionMap: ProductEditionMap): string {
  const editions = editionMap.get(appId) || [];
  for (const edition of editions) {
    if (edition.editionId === editionId) {
      return edition.billingId;
    }
  }
  return '';
}

export function getEditionName(appId: string, editionId: string, editionMap: ProductEditionMap): string {
  const editions = editionMap.get(appId) || [];
  for (const edition of editions) {
    if (edition.editionId === editionId) {
      return edition.editionName;
    }
  }
  return '';
}

export function getEditionDisplayName(appName: string, editionName?: string): string {
  if (!editionName) {
    return appName;
  }
  return `${appName} | ${editionName}`;
}

export function partnerHasRequiredFeatures(resellerItem: ResellerItem, partnerFeatures: string[]): boolean {
  if (
    !(
      resellerItem.restrictions &&
      resellerItem.restrictions.requiredFeatures &&
      resellerItem.restrictions.requiredFeatures.whitelist
    )
  ) {
    return true;
  }
  const whitelist = resellerItem.restrictions.requiredFeatures.whitelist;
  if (whitelist.length < 1) {
    return true;
  }
  if (!partnerFeatures || partnerFeatures.length === 0) {
    return false;
  }
  return whitelist.every((requiredFeature) => {
    return partnerFeatures.includes(requiredFeature);
  });
}

export function isCountryAllowedByResellerItemCountry(resellerItem: ResellerItem, country: string): boolean {
  if (!(resellerItem.restrictions && resellerItem.restrictions.country)) {
    return true;
  }
  const countryRestrictions = resellerItem.restrictions.country;

  // Prioritize blacklist

  if (countryRestrictions.blacklist && countryRestrictions.blacklist.length > 0) {
    const upperCased = countryRestrictions.blacklist.map((c) => c.toUpperCase());
    return upperCased.indexOf((country || '').toUpperCase()) < 0;
  }

  if (countryRestrictions.whitelist && countryRestrictions.whitelist.length > 0) {
    const upperCased = countryRestrictions.whitelist.map((c) => c.toUpperCase());
    return upperCased.indexOf((country || '').toUpperCase()) >= 0;
  }

  return true;
}

export function sortActivatableItems(
  activatableItems: ActivatableItem[],
  sortDirection: SortDirection,
): ActivatableItem[] {
  // Note: This is a base case for recursion
  if (!activatableItems || activatableItems.length <= 0) {
    return activatableItems;
  }
  if (sortDirection !== 'ASCENDING' && sortDirection !== 'DESCENDING') {
    return activatableItems;
  }

  let items = activatableItems.slice(); // Don't mutate original array
  items = items.map((item) => {
    item = { ...item }; // Don't mutate item
    item.children = sortActivatableItems(item.children, sortDirection);
    return item;
  });
  items.sort((child1, child2) => sortByName(child1, child2, sortDirection));
  return items;
}

function sortByName(child1: ActivatableItem, child2: ActivatableItem, sortDirection: SortDirection): number {
  const child1Name = child1 && child1.name ? child1.name : '';
  const child2Name = child2 && child2.name ? child2.name : '';
  return sortDirection === 'ASCENDING' ? child1Name.localeCompare(child2Name) : child2Name.localeCompare(child1Name);
}

export function doItemsHaveRestrictionReasons(activatableItems: ActivatableItem[]): boolean {
  if (!activatableItems || activatableItems.length <= 0) {
    return false;
  }
  return activatableItems.some((activatableItem) =>
    Boolean(activatableItem.restrictedReason || doItemsHaveRestrictionReasons(activatableItem.children)),
  );
}

export function filterRestrictedItems(activatableItems: ActivatableItem[]): ActivatableItem[] {
  if (!activatableItems || activatableItems.length <= 0) {
    return [];
  }
  const filteredItems = activatableItems.filter((activatableItem) => !activatableItem.restrictedReason);
  const filteredWithChildren = filteredItems.map((filteredItem) => {
    if (filteredItem.children) {
      filteredItem = { ...filteredItem }; // Don't mutate item
      filteredItem.children = filterRestrictedItems(filteredItem.children);
    }
    return filteredItem;
  });
  return filteredWithChildren;
}

/* Search rules
 * If parent matches, itself and all of its children are matched. (keeps all children)
 * If a child matches, only the children that match and the parent are matched. (limits children)
 */
export function searchActivatableItems(activatableItems: ActivatableItem[], searchTerm: string): ActivatableItem[] {
  if (!activatableItems || activatableItems.length <= 0 || !searchTerm) {
    return activatableItems || [];
  }

  searchTerm = searchTerm.trim().toUpperCase();
  const matchedAtRoot = activatableItems.filter((item) => {
    const uppercaseItemName = item.name.toUpperCase();
    const uppercaseItemId = item.appId.toUpperCase();
    const itemMatched = uppercaseItemName.indexOf(searchTerm) >= 0 || uppercaseItemId.indexOf(searchTerm) >= 0;
    if (itemMatched) {
      return true;
    }
  });

  const filteredChildren = activatableItems.map((item) => {
    item = { ...item, children: searchActivatableItems(item.children, searchTerm) };
    return item;
  });

  const matchedAtChildren = filteredChildren.filter((item) => item.children && item.children.length > 0);

  const m = new Map<string, ActivatableItem>();
  matchedAtRoot.forEach((item) => m.set(item.appId, item));
  matchedAtChildren.forEach((item) => {
    if (!m.has(item.appId)) {
      m.set(item.appId, item);
    }
  });

  return Array.from(m.values());
}

export function pageArray<T>(array: T[], pageEvent: PageEvent, startPageSize = 25): T[] {
  if (!array || array.length === 0) {
    return [];
  }

  let index: number;
  let pageSize: number;
  if (!pageEvent) {
    index = 0;
    pageSize = startPageSize;
  } else {
    index = pageEvent.pageIndex;
    pageSize = pageEvent.pageSize;
  }
  const offset = index * pageSize;
  return array.slice(offset, Math.min(pageSize + offset, array.length));
}

export function getSelectedItemRequiredAppIds(selectedId: string, activatableItems: ActivatableItem[]): string[] {
  const app = (activatableItems ?? []).find((activatableItem) => activatableItem.appId === selectedId);
  return app?.requiredAppIds ?? [];
}

export function getSelectedItemRequiredEditionIds(
  selectedId: string,
  activatableItems: ActivatableItem[],
): EditionIdentifier[] {
  const app = (activatableItems ?? []).find((activatableItem) => activatableItem.appId === selectedId);
  return app?.requiredEditions ?? [];
}

export function getSelectedItemParentId(appId: string, activatableItems: ActivatableItem[]): string {
  const parent = activatableItems.find((activatableItem) =>
    activatableItem.children.some((child) => child.appId === appId),
  );
  if (parent) {
    return parent.appId;
  }
  return '';
}

export function getSelectedAppRequiredApps(selectedId: string, activatableItems: ActivatableItem[]): ActivatableItem[] {
  const requiredAppIds = getSelectedItemRequiredAppIds(selectedId, activatableItems);
  return (activatableItems || []).filter((activatableItem) =>
    requiredAppIds.some((appIds) => appIds.includes(activatableItem.appId)),
  );
}

export function getSelectedAppDependents(selectedId: string, activatableItems: ActivatableItem[]): ActivatableItem[] {
  return (activatableItems || []).filter((activatableItem) =>
    (activatableItem.requiredAppIds || []).some((appId) => appId === selectedId),
  );
}

export function getSelectedItemChildren(productId: string, activatableItems: ActivatableItem[]): ActivatableItem[] {
  const selectedActivatableItem = activatableItems.filter((activatableItem) => activatableItem.appId === productId);
  if (selectedActivatableItem && selectedActivatableItem.length > 0) {
    return selectedActivatableItem[0].children;
  }
  return null;
}

// Signifier that selected events have been cleared; static interface
export interface ClearEvent {
  clear: true;
}

export interface CurrentSelection {
  event: SelectedItem | ClearEvent;
  items: ActivatableItem[];
  appStatus: ActivationStatusesInterface[];
}

export function updateSelectedItems(selectedItemsMap: SelectedItems, current: CurrentSelection): SelectedItems {
  if ((current.event as any).clear) {
    return new Map<string, QuantityEditionIdPair>() as SelectedItems; // clear all selections
  }

  const selected = current.event as SelectedItem;
  const data = {
    selected: selected,
    items: current.items,
    appStatus: current.appStatus,
  };
  const quantityEditionPair = {
    quantity: selected.quantity,
    editionId: selected.editionId,
  } as QuantityEditionIdPair;

  selectedItemsMap.set(selected.appId, quantityEditionPair);
  if (selected.quantity <= 0) {
    selectedItemsMap.delete(selected.appId);
  }

  selectedItemsMap = removeActivatedAppsWithoutDependentsOrAddons(selectedItemsMap, data);
  return selectedItemsMap;
}

export function areAddonDependenciesSatisfied(
  selectedItemsMap: SelectedItems,
  current: { selected: SelectedItem; items: ActivatableItem[]; appStatus: ActivationStatusesInterface[] },
): boolean {
  const selectedItem = current.selected;
  const app = current.items.find((item) => item.children.some((child) => child.appId === selectedItem.appId));
  if (!app) {
    return true;
  }

  return selectedItemsMap.has(app.appId) && selectedItemsMap.get(app.appId).quantity > 0;
}

export function isRequiredAppActive(productId: string, statuses: ActivationStatusesInterface[]): boolean {
  const appStatus = statuses.find((status) => status.appId === productId);
  const activated = appStatus?.status === AppAndAddonActivationStatus.ACTIVATED;
  const pending = appStatus?.status === AppAndAddonActivationStatus.PENDING;
  const cancelled = appStatus?.status === AppAndAddonActivationStatus.CANCELED;
  return appStatus?.appId === productId && (activated || pending || cancelled);
}

// Returns a list of the activatable items and their children as a flattened list.
export function flattenActivatableItems(activatableItems: ActivatableItem[]): ActivatableItem[] {
  return (
    activatableItems?.reduce(
      (result, activatableItem) => result.concat([activatableItem, ...(activatableItem.children || [])]),
      [],
    ) || []
  );
}

export function isAppMultiactivatable(appId: string, activatableItems: ActivatableItem[]): boolean {
  return activatableItems?.find((item) => item.appId === appId)?.allowMultipleActivations || false;
}

export function isTrialActivatableItem(appId: string, activatableItems: ActivatableItem[]): boolean {
  return activatableItems?.find((item) => item.appId === appId)?.isTrial || false;
}

export function isActivatableItemWithEditions(appId: string, activatableItems: ActivatableItem[]): boolean {
  return activatableItems?.find((item) => item.appId === appId)?.editionData?.usesEditions || false;
}

export function getFirstEditionIdForRequiredApp(appId: string, activatableItems: ActivatableItem[]): string {
  if (!appId || !activatableItems || activatableItems.length < 1) {
    return '';
  }

  const app = activatableItems.find((item) => item.appId === appId);
  if (app?.editionData?.editions?.length > 0) {
    return app.editionData.editions[0].editionId;
  }
  return '';
}

export function removeActivatedAppsWithoutDependentsOrAddons(
  selectedItemsMap: SelectedItems,
  current: { selected: SelectedItem; items: ActivatableItem[]; appStatus: ActivationStatusesInterface[] },
): SelectedItems {
  const items = current.items;
  const flattenedItems = flattenActivatableItems(items);
  selectedItemsMap.forEach((_, appId) => {
    if (
      !isRequiredAppActive(appId, current.appStatus) ||
      isAppMultiactivatable(appId, flattenedItems) ||
      isTrialActivatableItem(appId, flattenedItems) ||
      isActivatableItemWithEditions(appId, flattenedItems)
    ) {
      return;
    }

    const dependants = getSelectedAppDependents(appId, items);
    const addons = getSelectedItemChildren(appId, items);
    const hasDependants =
      dependants?.length > 0 && dependants.some((dependant) => selectedItemsMap.has(dependant.appId));
    const hasAddons = addons?.length > 0 && addons.some((addon) => selectedItemsMap.has(addon.appId));
    if (!hasDependants && !hasAddons) {
      selectedItemsMap.delete(appId);
    }
  });
  return selectedItemsMap;
}

export function removeActivatedParentIfChildIsRemoved(
  selectedItemsMap: SelectedItems,
  parentId: string,
  current: { selected: SelectedItem; items: ActivatableItem[]; appStatus: ActivationStatusesInterface[] },
): SelectedItems {
  const items = current.items;
  const selected = current.selected;
  const active = isRequiredAppActive(parentId, current.appStatus);
  if (parentId && selected.quantity === 0 && active) {
    const parentsChildren = getSelectedItemChildren(parentId, items);
    let keepParent = false;
    parentsChildren.forEach((child) => {
      if (selectedItemsMap.has(child.appId) && selectedItemsMap.get(child.appId).quantity > 0) {
        keepParent = true;
      }
    });
    if (!keepParent) {
      selectedItemsMap.delete(parentId);
    }
  }
  return selectedItemsMap;
}

export function filterResellerItemQuantity(
  resellerItemMap: Map<string, ResellerItem>,
  resellerQuantityMap: UniqueIdToItemIdAndQuantityMap,
  appStatuses: ActivationStatusesInterface[],
  activations?: Activation[],
): UniqueIdToItemIdAndQuantityMap {
  const filteredMap = new Map<string, ItemIdAndQuantity>() as UniqueIdToItemIdAndQuantityMap;
  Array.from(resellerQuantityMap.keys()).forEach((key) => {
    const item = resellerItemMap.get(key);
    if (!item) {
      return;
    }
    let isTrial = false;
    if (activations) {
      const activation = activations.find((act) => act.appId === getResellerItemUniqueId(item));
      isTrial = activation && activation.trial;
    }
    if (!item.allowMultipleActivations) {
      const isActive = isAppActive(getResellerItemUniqueId(item), appStatuses, isTrial);
      if (isActive) {
        return;
      }
    }

    filteredMap.set(key, resellerQuantityMap.get(key));
  });
  return filteredMap;
}

export function isAppActive(appId: string, appStatuses: ActivationStatusesInterface[], isTrial?: boolean): boolean {
  const as = (appStatuses || []).find((status) => status.appId === appId);
  if (!as) {
    return false;
  }
  if (as) {
    const active = as.status === AppAndAddonActivationStatus.ACTIVATED && !isTrial;
    const pending = as.status === AppAndAddonActivationStatus.PENDING && !isTrial;
    const cancelled = as.status === AppAndAddonActivationStatus.CANCELED && !isTrial;
    return active || pending || cancelled;
  }
}

export function doItemsRequireOrderForm(
  selectedItems: string[],
  activatableItems: ActivatableItem[],
  appStatus: ActivationStatusesInterface[],
): OrderFormType {
  const itemsThatRequiresDynamicOrderForm = (DynamicOrderFormAppIds || []).filter((dynamicAppId) => {
    return selectedItems.indexOf(dynamicAppId) >= 0;
  });
  const itemThatRequiresCustomOrderForm = (activatableItems || []).find((activatableItem) => {
    const isSelected =
      selectedItems.indexOf(activatableItem.appId) >= 0 &&
      (activatableItem.usesOrderForm || activatableItem.usesCustomPricing);
    const active = isAppActive(activatableItem.appId, appStatus);
    return isSelected && (!active || activatableItem.allowMultipleActivations);
  });
  if (itemsThatRequiresDynamicOrderForm.length > 0) {
    // TODO: resolve dynamic order forms in a chain when there are multiple apps
    if (itemsThatRequiresDynamicOrderForm.length > 1) {
      throw Error('Please activate these products separately.');
    }
    return OrderFormType.DYNAMIC;
  } else if (itemThatRequiresCustomOrderForm) {
    return OrderFormType.CUSTOM; // Might also be just COMMON
  } else {
    return OrderFormType.NONE;
  }
}

function getAppsWithoutParents(activatableItems: ActivatableItem[]): ActivatableItem[] {
  return (activatableItems || []).filter((item) => !item.parentAppId);
}

function buildChildMap(activatableItems: ActivatableItem[]): Map<string, ActivatableItem[]> {
  return (activatableItems || []).reduce((acc, current) => {
    const currentArray = acc.get(current.parentAppId) || [];
    currentArray.push(current);
    acc.set(current.parentAppId, currentArray);
    return acc;
  }, new Map<string, ActivatableItem[]>());
}

function setChildAppsOnClonedParentApps(
  parentApps: ActivatableItem[],
  childMap: Map<string, ActivatableItem[]>,
): ActivatableItem[] {
  return parentApps.map((app) => {
    const children = childMap.get(app.appId) || [];
    return { ...app, children: children };
  });
}

export function applyRestrictedReasonToChildren(parentApps: ActivatableItem[]): ActivatableItem[] {
  (parentApps || []).forEach((app) => {
    app.children = (app.children || []).map((child) => {
      return { ...child, restrictedReason: app.restrictedReason || child.restrictedReason };
    });
  });
  return parentApps;
}

export interface ResellerItemWithPricing {
  pricing: ProductPricing;
  pricingPurchaseCost?: PurchaseCost;
  resellerItem: ResellerItem;
  appPrice: AppPrice;
}

export interface ResellerItemPricingWithActiveStatus extends ResellerItemWithPricing {
  activeStatuses: ActivationStatusesInterface[];
}

function pairResellerItemsWithPricing(
  resellerItems: ResellerItem[],
  pricingMap: Map<string, ProductPricing>,
  appPriceMap: Map<string, AppPrices>,
): ResellerItemWithPricing[] {
  return (resellerItems || []).map((resellerItem) => {
    const pricing = pricingMap ? pricingMap.get(resellerItem.billingId) : undefined;

    const appPrice = appPriceMap
      ? appPriceMap.get(resellerItem.addonId || resellerItem.appId)?.pricesForContexts['wholesale']
      : undefined;
    return {
      resellerItem: resellerItem,
      pricing: pricing,
      appPrice: appPrice,
    };
  });
}

export function pairResellerPricingWithActiveStatuses(
  resellerItemsWithPricing: ResellerItemWithPricing[],
  activeStatuses: ActivationStatusesInterface[],
  pendingActivations: PendingActivationDisplay[],
): ResellerItemPricingWithActiveStatus[] {
  return (resellerItemsWithPricing || []).map((resellerItemWithPricing) => {
    const id = getResellerItemUniqueId(resellerItemWithPricing.resellerItem);
    let activeStates = [];
    if (activeStatuses) {
      activeStates = activeStatuses.filter((status) => status.appId === id);
    }
    let pendingStates = [];
    if (pendingActivations) {
      pendingStates = pendingActivations.filter((pending) => pending.itemId === id);
    }
    // pendingStates should contain activeStates (they both have the accounts stuff in them), so we should just be able to use pending states for all pending
    // remove all activeStates that are pending and append the converted pendingStates to the array
    // `pendingStatesWithoutRejected` will filter rejected product out since it's not "Pending". And allowed user activated the same product again once it rejected.
    // status 2 = PENDING
    const activeStatesWithoutPending = activeStates.filter((state) => state.status !== 2);
    const pendingStatesWithoutRejected = pendingStates.filter(
      (state) => state.status !== PendingActivationStatus.REJECTED,
    );
    const convertedPendingStates = pendingStatesWithoutRejected.map(
      transformPendingActivationDisplayToActivationStatus,
    );
    const statuses = [...activeStatesWithoutPending, ...convertedPendingStates];
    return {
      ...resellerItemWithPricing,
      activeStatuses: statuses,
    };
  });
}

/**
 * transformPendingActivationDisplayToActivationStatus transforms a PendingActivationDisplay into an ActivationStatusesInterface
 * @param {PendingActivationDisplay} pendingState - Display interface for pending activation
 * @returns {ActivationStatusesInterface}
 */
function transformPendingActivationDisplayToActivationStatus(
  pendingState: PendingActivationDisplay,
): ActivationStatusesInterface {
  return {
    appId: pendingState.itemId,
    // pending status
    status: 2,
  } as ActivationStatusesInterface;
}

export function addActivationToItems(
  activatableItems: ActivatableItem[],
  activations: Activation[],
): ActivatableItem[] {
  const currentTime = new Date();
  return (activatableItems || []).map((activatableItem) => {
    const foundActivation = activations.find((actiavtion) => actiavtion.appId === activatableItem.appId);
    activatableItem.isTrial = foundActivation && foundActivation.trial;
    if (foundActivation && foundActivation.deactivation) {
      activatableItem.trialStatus =
        foundActivation.deactivation > currentTime ? ActivationStatus.ACTIVATED : ActivationStatus.CANCELED;
    }
    if (foundActivation && foundActivation.editionId) {
      activatableItem.editionId = foundActivation.editionId;
      activatableItem.editionData.currentlyActiveEditionId = foundActivation.editionId;
    }
    return activatableItem;
  });
}

/**
 * assembleActiveProducts assembles product information for active items on the account
 * @param {ResellerItem[]} items - List of items that could be active
 * @param {AppsAndAddonsActivationStatusesInterface[]} appStatuses - Status objects for active items on the account
 * @returns {ProductBoxItems[]}
 */
export function assembleActiveProducts(
  items: ResellerItem[],
  appStatuses: ActivationStatusesInterface[],
): ProductBoxItems[] {
  const products: ProductBoxItems[] = [];
  appStatuses.forEach((appStatus) => {
    const activeItems = items.filter((item) => checkIfShouldTreatItemAsActive(item.addonId || item.appId, appStatus));
    activeItems.forEach((activeItem) => {
      products.push({
        appId: activeItem.addonId || activeItem.appId,
        name: activeItem.name,
        icon: activeItem.icon,
        count: appStatus.count,
        status: appStatus.status,
      } as ProductBoxItems);
    });
  });
  return products;
}

/**
 * checkIfShouldTreatItemAsActive determines whether or not an ite should be treated as active
 * @param {string} itemAppOrAddonId - Unique identifier of the item to be checked
 * @param {AppsAndAddonsActivationStatusesInterface} appStatus - Status object for the item on the account
 * @returns {boolean}
 */
export function checkIfShouldTreatItemAsActive(
  itemAppOrAddonId: string,
  appStatus: ActivationStatusesInterface,
): boolean {
  if (!appStatus) {
    return false;
  }
  const statusMatchesItemAppOrAddonId = itemAppOrAddonId === appStatus.appId;
  const isActivated = appStatus.status === AppAndAddonActivationStatus.ACTIVATED;
  const isPending = appStatus.status === AppAndAddonActivationStatus.PENDING;
  const isCancelled = appStatus.status === AppAndAddonActivationStatus.CANCELED;
  return statusMatchesItemAppOrAddonId && (isActivated || isPending || isCancelled);
}

/**
 * mergeActivationsIntoProducts merges a list of activations into products for currently active items on the business
 * @param {Activation[]} activations - Activations for currently active items on the business
 * @param {ProductBoxItems[]} products - Product representations of active items on the business
 * @param {ProductEditionMap} editionMap - Map of edition data for each product
 * @returns {ProductBoxItems[]}
 */
export function mergeActivationsIntoProducts(
  activations: Activation[],
  products: ProductBoxItems[],
  editionMap: ProductEditionMap,
): ProductBoxItems[] {
  const currentTime = new Date();

  const productBoxItems = products.map((product) => {
    // Handle pending activations
    if (product.status === ActivationStatus.PENDING) {
      return product;
    }

    return (
      activations
        .filter(
          // Find the correct addon or app
          (activation) =>
            activation.addonId ? activation.addonId === product.appId : activation.appId === product.appId,
        )
        // Dedupe (for addons)
        .filter(
          (activation, index, self) =>
            !activation.addonId || self.findIndex((a) => a.appId === activation.appId) === index,
        )
        // Create a ProductBoxItems with the descriptor
        .map((activation) => {
          if (activation) {
            product.isTrial = activation.trial;
            if (activation.deactivation) {
              product.trialStatus =
                activation.deactivation > currentTime ? ActivationStatus.ACTIVATED : ActivationStatus.CANCELED;
            }
            if (isValidEditionId(activation.editionId)) {
              product.editionId = activation.editionId;
              product.editionName = getEditionName(product.appId, activation.editionId, editionMap);
            }
            return { ...product, descriptor: activation.activationDescriptor };
          }
          return { ...product };
        })
    );
  });

  // Map over every product
  const result = productBoxItems.flat();

  return result;
}

/**
 * generateSelectedItemQueryParameters assembles the information about selected items into the SelectedItemQueryParams object
 * @param {ActivatableItem[]} activatableItems - List of activatable items for the account
 * @param {SelectedItems} selectedItems - Currently selected items with their quantities and edition information
 * @param {string} partnerId - Unique identifier of the partner
 * @param {string} accountGroupId - Unique identifier of the business
 * @param {ProductBoxItems[]} activeItems - Currently active items on the business
 * @param {string} navigationSource - String describing how the user got into the activation flow
 * @returns {SelectedItemQueryParams}
 */
export function generateSelectedItemQueryParameters(
  activatableItems: ActivatableItem[],
  selectedItems: SelectedItems,
  partnerId: string,
  accountGroupId: string,
  activeItems: ProductBoxItems[],
  navigationSource: string,
): SelectedItemQueryParams {
  const appIdsAndQuantities: ItemIdAndQuantity[] = [];
  const addonIdsAndQuantities: ItemIdAndQuantity[] = [];
  if (selectedItems) {
    selectedItems.forEach((quantityEditionPair, id) => {
      const isActive = activeItems.some((i) => i.appId === id && !i.isTrial);
      const item = activatableItems.find((i) => i.appId === id);
      if (item && quantityEditionPair.quantity > 0 && (!isActive || item.allowMultipleActivations)) {
        if (item.parentAppId) {
          addonIdsAndQuantities.push({ quantity: quantityEditionPair.quantity, addonId: item.appId });
        } else {
          appIdsAndQuantities.push({
            quantity: quantityEditionPair.quantity,
            appId: item.appId,
            editionId: quantityEditionPair.editionId,
          });
        }
      }
    });
  }

  return {
    accountGroupId: accountGroupId,
    appIds: JSON.stringify(appIdsAndQuantities),
    addonIds: JSON.stringify(addonIdsAndQuantities),
    partnerId: partnerId,
    navigationSource: navigationSource,
  };
}

/**
 * assembleAppCartItems assembles the information about selected items and their activation data for display in the cart
 * @param {SelectedItems} selectedItems - Currently selected items with their quantities and edition information
 * @param {ActivatableItem[]} activatableItems - List of activatable items for the account
 * @param {ProductEditionMap} editionMap - Map of product edition data
 * @param {AppsAndAddonsActivationStatusesInterface[]} appStatuses - Status objects for active items on the account
 * @param {Activation[]} activations - Activation instances for the active items on the account
 * @param {Date} currentTime - Current time
 * @returns {ActivatableItemCount[]}
 */
export function assembleAppCartItems(
  selectedItems: SelectedItems,
  activatableItems: ActivatableItem[],
  editionMap: ProductEditionMap,
  appStatuses: ActivationStatusesInterface[],
  activations: Activation[],
  currentTime: Date,
): ActivatableItemCount[] {
  const appCartItems: ActivatableItemCount[] = [];
  selectedItems.forEach((quantityEditionPair, id) => {
    const indexOfSelectedItem = activatableItems.findIndex((item) => item.appId === id);
    if (indexOfSelectedItem < 0) {
      return;
    }
    let appStatus = appStatuses.find((a) => a.appId === id);
    if (!appStatus || !appStatus.status) {
      appStatus = { status: ActivationStatus.NOT_SPECIFIED.valueOf() };
    }
    const activation = activations.find((act) => act.appId === id);
    const isTrial = activation && activation.trial;
    let trialStatus = ActivationStatus.ACTIVATED;
    if (activation && activation.deactivation) {
      trialStatus = activation.deactivation > currentTime ? ActivationStatus.ACTIVATED : ActivationStatus.CANCELED;
    }

    if (quantityEditionPair.editionId) {
      activatableItems[indexOfSelectedItem].billingId = getEditionBillingId(
        id,
        quantityEditionPair.editionId,
        editionMap,
      );
    }

    const appCartItem: ActivatableItemCount = {
      activatableItem: activatableItems[indexOfSelectedItem],
      quantity: quantityEditionPair.quantity,
      editionId: quantityEditionPair.editionId,
      editionName: getEditionName(id, quantityEditionPair.editionId, editionMap),
      status: appStatus.status.valueOf(),
      isTrial: isTrial,
      trialStatus: trialStatus,
    };
    appCartItems.push(appCartItem);
  });
  return appCartItems;
}

/**
 * assembleActivatablePackages assembles the information about available packages, selected items and their activation data
 * @param {Package[]} packages - Packages that partner has available for the account
 * @param {ActivatableItem[]} activatableItems - List of activatable items for the account
 * @param {AppsAndAddonsActivationStatusesInterface[]} appStatuses - Status objects for active items on the account
 * @param {Activation[]} activations - Activation instances for the active items on the account
 * @returns {ActivatablePackage[]}
 */
export function assembleActivatablePackages(
  packages: Package[],
  activatableItems: ActivatableItem[],
  appStatuses: ActivationStatusesInterface[],
  activations: Activation[],
): ActivatablePackage[] {
  const activatablePackages: ActivatablePackage[] = [];
  for (const pkg of packages || []) {
    const activatablePkg = generateActivatablePackage(pkg, activatableItems, appStatuses, activations);
    if (activatablePkg) {
      activatablePackages.push(activatablePkg);
    }
  }
  return activatablePackages;
}

/**
 * generateActivatablePackage constructs a single ActavatablePackage from available activation data on the account
 * @param {Package} pkg - Input package
 * @param {ActivatableItem[]} activatableItems - List of activatable items for the account
 * @param {AppsAndAddonsActivationStatusesInterface[]} appStatuses - Status objects for active items on the account
 * @param {Activation[]} activations - Activation instances for the active items on the account
 * @returns {ActivatablePackage}
 */
function generateActivatablePackage(
  pkg: Package,
  activatableItems: ActivatableItem[],
  appStatuses: ActivationStatusesInterface[],
  activations: Activation[],
): ActivatablePackage {
  if (pkg.lineItems && pkg.lineItems.lineItems && pkg.lineItems.lineItems.length > 0) {
    let restricted = false;
    const activatablePkg = initializeActivatablePackage(pkg);

    for (const li of pkg.lineItems.lineItems) {
      const activatableItemActivationPair = getActivationAndActivatableItem(li.id, activatableItems, activations);
      if (!restricted) {
        restricted = checkIfRestricted(activatableItemActivationPair.activatableItem);
      }
      const activatableItemCount = buildActivatableItemCount(
        li.id,
        activatableItemActivationPair,
        appStatuses,
        li.quantity,
        li,
      );
      if (activatableItemCount) {
        activatablePkg.activatableItemCount.push(activatableItemCount);
      }
    }

    activatablePkg.restricted = restricted;
    return activatablePkg;
  }
}

/**
 * initializeActivatablePackage initializes an ActivatablePackage from a Package
 * @param {Package} pkg - Activatable item to be checked
 * @returns {ActivatablePackage}
 */
function initializeActivatablePackage(pkg: Package): ActivatablePackage {
  if (!pkg) {
    return null;
  }
  return {
    name: pkg.name,
    packageId: pkg.packageId,
    icon: pkg.icon,
    activatableItemCount: [],
  };
}

/**
 * checkIfRestricted checks if an ActivatableItem is restricted
 * @param {ActivatableItem} activatableItem - Activatable item to be checked
 * @returns {boolean}
 */
export function checkIfRestricted(activatableItem: ActivatableItem): boolean {
  return !!(activatableItem && activatableItem.restrictedReason);
}

/**
 * getAppStatus returns the AppStatus object for a given app or addon id from a list of AppStatuses
 * @param {string} appOrAddonId - App or addon id of the product whose status is sought
 * @param {AppsAndAddonsActivationStatusesInterface[]} appStatuses - Status objects for active items on the account
 * @returns {AppsAndAddonsActivationStatusesInterface}
 */
export function getAppStatus(
  appOrAddonId: string,
  appStatuses: ActivationStatusesInterface[],
): ActivationStatusesInterface {
  if (!appStatuses) {
    return { status: ActivationStatus.NOT_SPECIFIED.valueOf() };
  }
  let appStatus = appStatuses.find((a) => a.appId === appOrAddonId);
  if (!appStatus || !appStatus.status) {
    appStatus = { status: ActivationStatus.NOT_SPECIFIED.valueOf() };
  }
  return appStatus;
}

export interface ActivatableItemActivationPair {
  activatableItem: ActivatableItem;
  activation: Activation;
}

/**
 * getActivationAndActivatableItem returns the ActivatableItem and Activation objects for a given app or addon id from list of those objects
 * @param {string} appOrAddonId - App or addon id of the product whose status is sought
 * @param {ActivatableItem[]} activatableItems - List of activatable items for the account
 * @param {Activation[]} activations - Activation instances for the active items on the account
 * @returns {ActivatableItemActivationPair}
 */
export function getActivationAndActivatableItem(
  appOrAddonId: string,
  activatableItems: ActivatableItem[],
  activations: Activation[],
): ActivatableItemActivationPair {
  let activatableItem = null;
  let activation = null;
  if (activatableItems) {
    activatableItem = activatableItems.find((item) => item.appId === appOrAddonId) || null;
  }
  if (activations) {
    activation = activations.find((act) => act.appId === appOrAddonId) || null;
  }
  return {
    activatableItem,
    activation,
  };
}

/**
 * constructActivatableItemCount returns the ActivatableItem and Activation objects for a given app or addon id from list of those objects
 * @param {string} appOrAddonId - App or addon id of the product whose status is sought
 * @param {ActivatableItemActivationPair} activatableItemActivationPair - Pair of activatable item and activation
 * @param {number} quantity - Quantity of app or addon in the package
 * @returns {ActivatableItemCount}
 */
export function buildActivatableItemCount(
  appOrAddonId: string,
  activatableItemActivationPair: ActivatableItemActivationPair,
  appStatuses: ActivationStatusesInterface[],
  quantity: number,
  lineItem: LineItem,
): ActivatableItemCount {
  const appStatus = getAppStatus(appOrAddonId, appStatuses);
  let trial = false;
  const { activatableItem, activation } = activatableItemActivationPair;
  if (activation) {
    trial = activation.trial;
  }

  if (activatableItem) {
    activatableItem.isTrial = trial;
    return {
      activatableItem: activatableItem,
      quantity: quantity || 1,
      status: appStatus.status.valueOf(),
      editionId: lineItem ? lineItem.editionId : '',
      isTrial: trial,
    };
  }
  return null;
}

/**
 * extractQueryParameters extracts the information about selected items contained in the query parameters
 * @param {Params} parameters - Query parameters containing information about selected items
 * @returns {UniqueIdToItemIdAndQuantityMap}
 */
export function extractQueryParameters(parameters: Params): UniqueIdToItemIdAndQuantityMap {
  if (!parameters) {
    return new Map<string, ItemIdAndQuantity>();
  }
  let appIdsAndQuantity: ItemIdAndQuantity[] = [];
  if (parameters.appIds) {
    appIdsAndQuantity = JSON.parse(parameters.appIds);
    if (appIdsAndQuantity.length) {
      const firstItem = appIdsAndQuantity[0];
      const probablyNotType = typeof firstItem.appId === 'undefined' && firstItem.addonId === 'undefined';
      if (probablyNotType) {
        // TODO get rid of this catch by editing how account create sends its url params
        appIdsAndQuantity = parameters.appIds.split(',');
      }
    }
  }
  const addonIdsAndQuantity: ItemIdAndQuantity[] = parameters.addonIds ? JSON.parse(parameters.addonIds) : [];

  return appIdsAndQuantity.concat(addonIdsAndQuantity).reduce((m, itemQuantity) => {
    m.set(getResellerItemUniqueId(itemQuantity), itemQuantity);
    return m;
  }, new Map<string, ItemIdAndQuantity>() as UniqueIdToItemIdAndQuantityMap);
}

/**
 * convertExtractedQueryParamsToSelectedItems converts extracted information about selected items to selected items form
 * @param {UniqueIdToItemIdAndQuantityMap} itemQuantityMap - Information about selected items
 * @returns {SelectedItem[]}
 */
export function convertExtractedQueryParamsToSelectedItems(
  itemQuantityMap: UniqueIdToItemIdAndQuantityMap,
): SelectedItem[] {
  if (!itemQuantityMap) {
    return [];
  }
  return Array.from(itemQuantityMap.keys()).reduce((arr, key) => {
    // Some pro editions have an empty edition ID.
    let editionId = itemQuantityMap.get(key).editionId;
    if (!editionId && editionId !== '') {
      editionId = null;
    }
    arr.push({ appId: key, quantity: itemQuantityMap.get(key).quantity, editionId: editionId } as SelectedItem);
    return arr;
  }, []);
}

/**
 * extractIdsOfItemsUsingEditions extracts the app ids of reseller items that use editions
 * @param {ResellerItem[]} resellerItems - Query parameters containing information about selected items
 * @returns {string[]}
 */
export function extractIdsOfItemsUsingEditions(resellerItems: ResellerItem[]): string[] {
  if (!resellerItems) {
    return [];
  }
  return resellerItems
    .filter((ri) => !ri.addonId)
    .filter((ri) => ri.usesEditions)
    .map((ri) => ri.appId);
}

/**
 * getBillingId extracts the billingId of a resellerItem, it can be from the resellerItem if the resellerItem is not editioned,
 * or from a list of editions according to the editionId on the resellerItem.
 * @param {ResellerItem} item - Query parameters containing information about selected reseller item
 * @param {Edition[]} productEditions - Query parameters containing information about editions
 * @returns {string}
 */
export function getBillingId(item: ResellerItem, productEditions: Edition[]): string {
  // If it's not an edition, we don't need to search for the billing ID
  if (!item.editionId) {
    return item.billingId;
  }

  const edition = productEditions.find((e) => e.editionId === item.editionId);
  return edition ? edition.billingId : item.billingId;
}
